import { Trans } from '@lingui/react/macro';
import { Loader2 } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { PageHeader } from '@/common/components/page-header/PageHeader';
import { Button } from '@/common/components/ui/button';
import { Skeleton } from '@/common/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useMemo } from 'react';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import { telemetryQueries } from '@/features/telemetry/queries';
import { resolveRange, type OverviewRangeSearch } from '@/features/telemetry/utils/resolveRange';
import { formatRangeSummary } from '@/features/telemetry/utils/formatRangeSummary';
import type { RangePreset } from '@/features/telemetry/config';
import { EmsStatusBar } from '@/features/telemetry/components/ems-status-bar/EmsStatusBar';
import { RangeSelector } from '@/features/telemetry/components/range-selector/RangeSelector';
import { DeviceCard } from '@/features/telemetry/components/device-card/DeviceCard';
import { DeviceGridSkeleton } from '@/features/telemetry/components/device-grid-skeleton/DeviceGridSkeleton';

export interface OverviewPageProps {
  search: OverviewRangeSearch;
  onSearchChange: (next: OverviewRangeSearch) => void;
}

export function OverviewPage({ search, onSearchChange }: OverviewPageProps) {
  const { activeSupplyPoint } = useSupplyPoint();
  const supplyPointId = activeSupplyPoint!.id;

  // Refreshed whenever `search` actually changes (a new preset, a new custom range,
  // browser back/forward, a fresh load) rather than once at mount — otherwise every
  // preset click would keep resolving "24h" etc. relative to however long ago the
  // tab happened to be opened, not the moment the user picked it.
  const searchKey = `${search.preset ?? ''}|${search.from ?? ''}|${search.to ?? ''}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on searchKey, not search
  const now = useMemo(() => new Date(), [searchKey]);
  const range = resolveRange(search, now);

  const { data: fields } = useSuspenseQuery(telemetryQueries.fields(supplyPointId));
  const emsStatusQuery = useQuery(telemetryQueries.emsStatus(supplyPointId));
  const seriesQuery = useQuery(
    telemetryQueries.series(supplyPointId, {
      from: range.from,
      to: range.to,
      resolution: range.resolution,
    })
  );
  const handleRangeChange = (next: { preset: RangePreset } | { from: Date; to: Date }) => {
    onSearchChange(
      'preset' in next
        ? { preset: next.preset }
        : { from: next.from.toISOString(), to: next.to.toISOString() }
    );
  };

  // `seriesQuery.data` bundles resolution/from/to alongside result (see queries.ts),
  // so it's always a self-consistent pairing — reading these instead of the live
  // `range` for DeviceCard's props is what keeps them referentially stable (and thus
  // skipped by the React Compiler's memoization) for the whole "click to new data
  // arriving" window, rather than changing the instant a range change is requested.
  const seriesData = seriesQuery.data;
  const devices = seriesData?.result ?? [];
  const isInitialLoad = seriesQuery.isPending;
  const isRefetching = seriesQuery.isFetching && !seriesQuery.isPending;

  return (
    <div className="space-y-5 pb-10">
      <PageHeader
        title={<Trans>Overview</Trans>}
        subtitle={<Trans>Device monitoring and system status</Trans>}
      />

      {emsStatusQuery.isPending ? (
        <Skeleton className="h-11 w-full rounded-lg" />
      ) : emsStatusQuery.isError ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted px-4 py-2.5 text-sm text-muted-foreground">
          <span
            className="size-2 shrink-0 rounded-full bg-muted-foreground/40"
            aria-hidden="true"
          />
          <Trans>Status unavailable</Trans>
        </div>
      ) : (
        <EmsStatusBar
          lastMessageAt={emsStatusQuery.data.lastMessageAt}
          isOnline={emsStatusQuery.data.isOnline}
          now={now}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="hidden items-center gap-1.5 text-xs tabular-nums text-muted-foreground sm:flex">
          {formatRangeSummary(range.from, range.to)}
          {isRefetching && <Loader2 className="size-3 animate-spin" aria-label={t`Loading`} />}
        </p>
        <RangeSelector value={range} onChange={handleRangeChange} />
      </div>

      {isInitialLoad ? (
        <DeviceGridSkeleton />
      ) : seriesQuery.isError ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">
            <Trans>Could not load telemetry for this range.</Trans>
          </p>
          <Button variant="outline" size="sm" onClick={() => seriesQuery.refetch()}>
            <Trans>Retry</Trans>
          </Button>
        </div>
      ) : devices.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">
            <Trans>No devices are connected to this supply point yet.</Trans>
          </p>
          <Button asChild size="sm">
            <Link to="/devices/new">{t`Add device`}</Link>
          </Button>
        </div>
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-5 md:grid-cols-2 transition-opacity',
            isRefetching && 'pointer-events-none opacity-60'
          )}
        >
          {devices.map((device) => (
            <DeviceCard
              key={device.deviceId}
              device={device}
              fields={fields}
              from={seriesData!.from}
              to={seriesData!.to}
              resolution={seriesData!.resolution}
            />
          ))}
        </div>
      )}
    </div>
  );
}
